"use client";

import { useState } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';
import { Button } from '@/components/ui/button';
import { Mail, Check, AlertCircle } from 'lucide-react';

interface NewsletterSectionProps {
  config: {
    headline?: string;
    subheadline?: string;
    placeholder?: string;
    buttonText?: string;
    privacyText?: string;
    benefits?: string[];
  };
  locale: string;
}

export function NewsletterSection({ config, locale }: NewsletterSectionProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      // TODO: Connect to backend API
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          language_code: locale,
          source: 'homepage'
        }),
      });

      if (response.ok) {
        setStatus('success');
        setMessage('Thank you! Check your email to confirm your subscription.');
        setEmail('');
      } else {
        const data = await response.json();
        setStatus('error');
        setMessage(data.error || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-green-600" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnpNNiAzNGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6TTM2IDM0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" />
      
      <div className="relative z-10 container mx-auto max-w-4xl">
        <div className="text-center mb-12">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-xl mb-6">
            <Mail className="w-8 h-8 text-white" />
          </div>

          {/* Headline */}
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {config.headline || 'Stay Updated on PostgreSQL Backup Best Practices'}
          </h2>
          
          {/* Subheadline */}
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            {config.subheadline || 'Get monthly insights on backup strategies, compliance updates, and new features. Unsubscribe anytime.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-xl mx-auto mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={config.placeholder || 'Enter your email address'}
              disabled={status === 'loading' || status === 'success'}
              className="flex-1 px-6 py-4 rounded-xl bg-white/10 backdrop-blur-xl border-2 border-white/20 text-white placeholder-white/60 focus:border-white focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <Button
              type="submit"
              size="lg"
              disabled={status === 'loading' || status === 'success'}
              className="px-8 py-4 h-auto bg-white text-blue-600 hover:bg-blue-50 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  Subscribing...
                </span>
              ) : status === 'success' ? (
                <span className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Subscribed!
                </span>
              ) : (
                config.buttonText || 'Subscribe'
              )}
            </Button>
          </div>

          {/* Status Messages */}
          {message && (
            <div className={`mt-4 p-4 rounded-xl flex items-start gap-3 ${
              status === 'success' 
                ? 'bg-green-500/20 border border-green-400/30' 
                : 'bg-red-500/20 border border-red-400/30'
            }`}>
              {status === 'success' ? (
                <Check className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-sm ${status === 'success' ? 'text-green-100' : 'text-red-100'}`}>
                {message}
              </p>
            </div>
          )}
        </form>

        {/* Benefits */}
        {config.benefits && config.benefits.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
            {config.benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-3 text-white">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-sm">{benefit}</span>
              </div>
            ))}
          </div>
        )}

        {/* Privacy Text */}
        <p className="text-center text-sm text-blue-100/80">
          {config.privacyText || 'We respect your privacy. No spam, ever. GDPR compliant.'}
        </p>
      </div>
    </section>
  );
}
